import { Alert, Box, Link, List, ListItem, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * User responsibility notice (spec §17.2).
 *
 * The verbatim notice in §17.2 must be visible somewhere users land.
 * This page hosts the canonical, full-length version. Compressed
 * banners that summarize it (e.g. on the homepage) link here for the
 * complete text.
 *
 * NOTE: This is placeholder copy assembled from the spec. Final
 * wording must be reviewed by counsel before launch — see the
 * TODO(legal-review) markers below.
 */
export function UserResponsibilityPage() {
  return (
    <Stack spacing={3} sx={{ maxWidth: 'md' }}>
      <Typography variant="h1" component="h1">
        User responsibility
      </Typography>

      {/* §17.2 verbatim — the spec mandates the exact wording of this paragraph. */}
      <Alert severity="info" role="region" aria-label="User responsibility notice">
        <Typography variant="body1">
          Round Recon is an ammunition search and comparison platform. Purchases are completed on
          third-party retailer websites. Users are responsible for complying with all applicable
          federal, state, and local laws and confirming that any ammunition is lawful for them to
          purchase, possess, and receive.
        </Typography>
      </Alert>

      <Box component="section" aria-labelledby="what-we-do">
        <Typography id="what-we-do" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
          What Round Recon does
        </Typography>
        <Typography variant="body1">
          We aggregate publicly-listed ammunition inventory from independent retailers, normalize
          inconsistent product data, and make it searchable. When you click through to a listing,
          you leave Round Recon and complete your transaction on the retailer&apos;s site under that
          retailer&apos;s terms.
        </Typography>
      </Box>

      <Box component="section" aria-labelledby="what-we-dont-do">
        <Typography
          id="what-we-dont-do"
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.4rem', mb: 1 }}
        >
          What Round Recon does not do
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            We do not process payments, store payment information, or collect tax.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            We do not verify your age or legal eligibility to purchase ammunition. The retailer you
            transact with is responsible for those checks.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            We do not ship products, accept returns, or process refunds.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            We do not guarantee that inventory shown in a listing is in stock, accurately priced, or
            legal to ship to your address. We display freshness signals (fresh, aging, stale) and
            last-checked timestamps so you can judge for yourself.
          </ListItem>
        </List>
      </Box>

      <Box component="section" aria-labelledby="your-obligations">
        <Typography
          id="your-obligations"
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.4rem', mb: 1 }}
        >
          Your obligations
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Comply with federal, state, and local laws governing the purchase, possession, and
            shipment of ammunition.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Confirm that any product you order can be legally shipped to your address.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Provide accurate information to retailers when completing your purchase.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Store and use ammunition safely and lawfully.
          </ListItem>
        </List>
      </Box>

      <Box component="section" aria-labelledby="jurisdiction">
        <Typography
          id="jurisdiction"
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.4rem', mb: 1 }}
        >
          Jurisdiction-specific notes
        </Typography>
        {/* TODO(legal-review): the final list of state-specific restrictions
            (e.g. California, New York, Massachusetts, Hawaii, NJ) must be
            confirmed with counsel before launch. */}
        <Typography variant="body1">
          Certain U.S. states restrict the type, quantity, or method of ammunition purchase
          permitted. Round Recon does not enforce these restrictions on your behalf — you remain
          solely responsible for verifying that an order is lawful where you reside. If you have
          questions, contact the appropriate state or local authority before purchasing.
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary">
        See also our{' '}
        <Link component={RouterLink} to="/terms">
          Terms of use
        </Link>{' '}
        and{' '}
        <Link component={RouterLink} to="/privacy">
          Privacy policy
        </Link>
        .
      </Typography>
    </Stack>
  );
}

export default UserResponsibilityPage;
